import React, { useEffect, useState } from 'react';
import { Card, Divider, InputNumber, message } from 'antd';
import CommonTable from 'components/CommonTable';
import type { ColumnsType } from 'antd/es/table';
import { updateServiceCustomization } from 'api/calendarSetting';
import ColorSelector from './ColorSelector';
import { useCalendarCustomize, IServiceData } from 'hooks/useCalendarCustomize';
import s from './s.module.less';

const CalendarCustomizePage = () => {
    const [serviceData, loading, refetch] = useCalendarCustomize();
    const [data, setData] = useState<IServiceData[]>([]);

    useEffect(() => {
        setData([...serviceData]);
    }, [serviceData]);

    const updateCustomization = async (duration?: number, info?: IServiceData[]) => {
        const newData = info || data;
        if (!newData || newData.length === 0) return;
        if (duration !== undefined && duration < 15) {
            message.error('Duration must be at least 15 minutes');
            return;
        }
        try {
            const result = await updateServiceCustomization(newData.map((item) => ({
                serviceType: item.serviceType,
                color: item.color,
                duration: item.duration,
                id: item.id,
            })));
            if (result.error) {
                message.error(result.error);
            } else {
                refetch();
            }
        } catch (error) {
            console.error(error);
        }
    };

    const columns: ColumnsType<IServiceData> = [
        {
            title: 'NAME',
            dataIndex: 'displayName',
            key: 'displayName',
            align: 'left',
            width: '250px',
            render: (text) => (
                <div className={s.nameColumn}>
                    <span className={s.serviceName}>{text}</span>
                </div>
            ),
        },
        {
            title: 'DESCRIPTION',
            dataIndex: 'description',
            key: 'description',
            align: 'left',
            render: (text) => (
                <p className={s.descriptionText}>{text}</p>
            ),
        },
        {
            title: 'DURATION',
            dataIndex: 'duration',
            key: 'duration',
            align: 'left',
            width: '120px',
            render: (text, record) => (
                <div className="flex items-center">
                    <InputNumber
                        value={text}
                        className={s.durationInput}
                        onChange={(value) => {
                            const newData = data.map((item) => {
                                if (item.serviceType === record.serviceType) {
                                    return { ...item, duration: value };
                                }
                                return item;
                            });
                            setData(newData);
                        }}
                        onBlur={() => {
                            updateCustomization(record.duration);
                        }}
                        controls={false}
                    />
                    <span className="ml-2">mins</span>
                </div>
            ),
        },
        {
            title: 'COLOR',
            dataIndex: 'color',
            key: 'color',
            align: 'left',
            width: '80px',
            render: (_, record) => (
                <div className={s.colorSelector}>
                    <ColorSelector
                        value={record.color}
                        onChange={(color) => {
                            const newData = data.map((item) => {
                                if (item.serviceType === record.serviceType) {
                                    return { ...item, color };
                                }
                                return item;
                            });
                            setData(newData);
                            updateCustomization(_, newData);
                        }}
                    />
                </div>
            ),
        },
    ];

    return (
        <Card className={s.customizeContainer}>
            <h2 className={s.pageTitle}>Manage your services</h2>
            <Divider />

            <CommonTable
                className={s.servicesTable}
                bordered={false}
                loading={loading}
                columns={columns}
                data={data}
                pagination={false}
                rowKey={(record) => record.serviceType}
            />
        </Card>
    );
};

export default CalendarCustomizePage;
